import React from "react";
import { BookingLanguage } from "@/types/booking";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Plus } from "lucide-react";
import { t } from "@/locales";
import { SafeMarkdown } from "@/components/product-step/card/SafeMarkdown";
import { useMarket } from "@/hooks/useMarket";
import { marketHasImaging } from "@/config/market";

interface FAQProps {
  language: BookingLanguage;
  packageType?: string;
  /** Membership cadence page → show the membership FAQ instead of the
   *  scan/blood FAQ. */
  membership?: boolean;
}

/** Membership FAQ — keys live in the locale files (`membershipCadence.faqQ*` / `faqA*`),
 *  sourced from the marketing /membership page content (EN/DE/FR patches). */
const buildMembershipFAQ = (
  language: BookingLanguage,
): Record<string, { question: string; answer: string }> =>
  Object.fromEntries(
    Array.from({ length: 8 }, (_, idx) => {
      const i = idx + 1;
      return [
        `q${i}`,
        {
          question: t(language, `membershipCadence.faqQ${i}`),
          answer: t(language, `membershipCadence.faqA${i}`),
        },
      ];
    }),
  );

const buildFAQTranslations = (
  language: BookingLanguage,
  sellsImaging: boolean,
) => {
  return {
    faqTitle: t(language, "faq.title"),
    needAdvice: t(language, "faq.needAdvice"),
    letsTalk: t(language, "faq.letsTalk"),
    shared: {},
    // Questions people ask while choosing a package. Five at most, so the
    // advisory board below the FAQ stays in view. Answers are markdown.
    mri: {
      "mri-package": {
        question: t(language, "faq.mri.package.question"),
        answer: t(language, "faq.mri.package.answer"),
      },
      "mri-who-for": {
        question: t(language, "faq.mri.whoFor.question"),
        answer: t(language, "faq.mri.whoFor.answer"),
      },
      "mri-findings": {
        question: t(language, "faq.mri.findings.question"),
        answer: t(language, "faq.mri.findings.answer"),
      },
      "mri-insurance": {
        question: t(language, "faq.mri.insurance.question"),
        answer: t(language, "faq.mri.insurance.answer"),
      },
      "mri-claustrophobia": {
        question: t(language, "faq.mri.claustrophobia.question"),
        answer: t(language, "faq.mri.claustrophobia.answer"),
      },
    },
    blood: {
      // Points blood bookers to the MRI check-ups, so only where we sell them.
      ...(sellsImaging && {
        "blood-add-mri": {
          question: t(language, "faq.blood.addMri.question"),
          answer: t(language, "faq.blood.addMri.answer"),
        },
      }),
      "after-booking": {
        question: t(language, "faq.blood.afterBooking.question"),
        answer: t(language, "faq.blood.afterBooking.answer"),
      },
      "appointment-change": {
        question: t(language, "faq.blood.appointmentChange.question"),
        answer: t(language, "faq.blood.appointmentChange.answer"),
      },
      "blood-preparation": {
        question: t(language, "faq.blood.preparation.question"),
        answer: t(language, "faq.blood.preparation.answer"),
      },
      "blood-safety": {
        question: t(language, "faq.blood.safety.question"),
        answer: t(language, "faq.blood.safety.answer"),
      },
    },
  };
};

const FAQ = ({
  language,
  packageType = "mri",
  membership = false,
}: FAQProps) => {
  const { market } = useMarket();
  const translations = buildFAQTranslations(language, marketHasImaging(market));
  const typeQuestions =
    packageType === "blood-only" ? translations.blood : translations.mri;
  const questions = membership
    ? buildMembershipFAQ(language)
    : { ...translations.shared, ...typeQuestions };

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-[1120px] mx-auto">
        {/* Two-column layout on desktop */}
        <div className="flex flex-col md:flex-row md:gap-16 lg:gap-24">
          {/* Left: Title */}
          <div className="mb-8 md:mb-0 md:w-[280px] lg:w-[320px] flex-shrink-0">
            <h2 className="text-[28px] md:text-[36px] leading-[1.2] font-sans font-normal text-neutral-950">
              {translations.faqTitle}
            </h2>
          </div>

          {/* Right: Accordion */}
          <div className="flex-1">
            <Accordion type="single" collapsible className="w-full">
              {Object.entries(questions).map(([key, faq]) => (
                <AccordionItem
                  key={key}
                  value={key}
                  className="border-b-0 border-t border-gray-200"
                >
                  <AccordionTrigger className="text-left text-base font-normal text-gray-900 py-5 hover:no-underline hover:text-gray-700 transition-colors [&>svg.lucide-chevron-down]:hidden normal-case group">
                    <span className="flex-1 pr-4">{faq.question}</span>
                    <span className="flex items-center justify-center">
                      <Plus className="h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 group-data-[state=open]:rotate-45" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-500 pb-5 pt-0 leading-relaxed">
                    <SafeMarkdown content={faq.answer} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
